/** @format */

import { useState, useEffect, useRef } from 'react';
import type { Widget } from 'src/types/widget';
import Page from 'src/components/Page';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Container } from '../../components/container';
import { GetWidget } from 'src/components/widget';
import { dummyWidget } from './dummy/dummyWidgets-news';
import { calcSize, getBreakpoint } from 'src/utils/widget';
import { generateLayout } from 'src/components';
import useGridConfigs from 'src/hooks/useGridConfigs';
import Breadcrumb from 'src/components/Breadcrumb';
import _ from 'lodash';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

let totalWidthOfItems = 0;
const changeTotalWidthOfItems = data => {
  totalWidthOfItems += data;
};

const debouncedUpdate = _.debounce(callback => {
  callback();
}, 100);

export default function NewsWidgetPreview() {
  const gridConfigs = useGridConfigs();
  const [globalLayouts, setLayouts] = useState({});
  const [currentDrag, setCurrentDrag] = useState(null);
  const [itemPositions, setItemPositions] = useState([]);
  const [widgets, setWidgets] = useState<Widget[]>(dummyWidget as Widget[]);
  const [currentBreakPoint, setCurrentBreakPoint] = useState(null);
  const gridLayoutRef = useRef(null);

  useEffect(() => {
    const calculateItemPositions = () => {
      const positions = [];
      let currentX = 0;
      let currentY = 0;
      let maxHeightInRow = 0;

      if (widgets.length) {
        widgets.forEach((item, i) => {
          const itemWidth = calcSize(item.size, item).w;
          const itemHeight = calcSize(item.size, item).h;

          const CONTACT_WIDGET = 'contact';
          if (
            !(
              item.type === CONTACT_WIDGET &&
              widgets[i - 1] &&
              widgets[i - 1].type &&
              widgets[i - 1].type === CONTACT_WIDGET
            ) &&
            currentX + itemWidth > 12
          ) {
            currentX = 0;
            currentY += maxHeightInRow;
            maxHeightInRow = 0;
          }

          positions.push({
            x: currentX,
            y: currentY
          });

          currentX += itemWidth;
          if (itemHeight > maxHeightInRow) {
            maxHeightInRow = itemHeight;
          }
        });
      }
      return positions;
    };

    if (widgets.length && itemPositions.length === 0) setItemPositions(calculateItemPositions());
    if (widgets.length && itemPositions.length) {
      const layoutArr = [];
      const newWigets = [];
      widgets.forEach((item, index) => {
        const dataGrid = generateLayout(item, index, totalWidthOfItems, changeTotalWidthOfItems, itemPositions);
        layoutArr.push(dataGrid);
        newWigets.push({
          ...item,
          x: dataGrid.x,
          y: dataGrid.y,
          w: dataGrid.w,
          minW: dataGrid.minW,
          maxW: dataGrid.maxW,
          h: dataGrid.h,
          minH: dataGrid.minH,
          maxH: dataGrid.maxH
        });
      });
      setWidgets(newWigets);
      if (currentBreakPoint) setLayouts({ ...globalLayouts, [currentBreakPoint]: layoutArr });
      else setLayouts({ ...globalLayouts, [Object.keys(globalLayouts)[0]]: layoutArr });
    }
  }, [currentBreakPoint, itemPositions]);

  const onLayoutChange = (currentLayout, allLayouts) => {
    if (Object.keys(globalLayouts).length === 0) setLayouts(allLayouts);
    debouncedUpdate(() => {
      if (gridLayoutRef.current) gridLayoutRef.current.classList.add('dashboard-widgets-mobile');
    });
  };
  const onBreakpointChange = (newBreakpoint, newCols) => {
    setCurrentBreakPoint(newBreakpoint);
  };
  const onWidthChange = (containerWidth, margin, cols, containerPadding) => {};

  const onDrop = () => {};
  const onDropDragOver = () => {};
  const onDragStart = () => {};
  const onDrag = () => {};
  const onDragStop = () => {};
  const onResize = (layout, oldItem, newItem, placeholder, e, element) => {
    const newWidgets = [...widgets];
    const newWidgetItem = newWidgets.find(w => w.i === newItem.i);
    newWidgetItem.w = newItem.w;
    newWidgetItem.h = newItem.h;

    // Avoid free resize
    const itemIndex = layout.findIndex(w => w.i === newItem.i);
    // START For normal widget
    if (getBreakpoint() === 'lg') {
      if (newItem.w > 8 && newItem.w < 11) newWidgetItem.w = 8;
      if (newItem.w > 10) newWidgetItem.w = 12;
    }
    // END For normal widget
    // START For video widget
    if (newWidgetItem.type === 'video') {
      if (getBreakpoint() === 'xxl') {
        if (newItem.h === 4) newWidgetItem.h = 5;
        if (newItem.w === 4 && newItem.h === 5) newWidgetItem.h = 6;
        if (newItem.w === 5 && newItem.h === 5) newWidgetItem.h = 6;
      }
      if (getBreakpoint() === 'xl') {
        if (newItem.w === 4 && newItem.h === 4) newWidgetItem.h = 5;
        if (newItem.w === 5 && newItem.h === 4) newWidgetItem.h = 5;
        if (newItem.w === 7 && newItem.h === 4) newWidgetItem.h = 5;
        if (newItem.w === 8 && newItem.h < 6) newWidgetItem.h = 6;
        if (newItem.w === 9 && newItem.h < 6) newWidgetItem.h = 6;
        if (newItem.h > 6) newWidgetItem.h = 6;
      }
      if (getBreakpoint() === 'lg') {
        newWidgetItem.maxH = 6;
        if (newItem.w === 5 && newItem.h < 5) newWidgetItem.h = 5;
        if (newItem.w === 6 && newItem.h < 5) newWidgetItem.h = 5;
        if (newItem.w === 12) newWidgetItem.h = 6;
      }
      if (getBreakpoint() === 'md') {
        newWidgetItem.minH = 4;
        newWidgetItem.maxH = 4;
        newWidgetItem.h = 4;
      }
      if (getBreakpoint() === 'sm') {
        newWidgetItem.minH = 5;
        newWidgetItem.maxH = 5;
        newWidgetItem.h = 5;
      }
      if (getBreakpoint() === 'xs') {
        newWidgetItem.minH = 4;
        newWidgetItem.maxH = 4;
        newWidgetItem.h = 4;
      }
    }
    // END For video widget

    layout[itemIndex].w = newWidgetItem.w;
    layout[itemIndex].h = newWidgetItem.h;
    layout[itemIndex].maxH = newWidgetItem.maxH;
    layout[itemIndex].minH = newWidgetItem.minH;

    setWidgets(newWidgets);
    if (typeof globalLayouts[getBreakpoint()] === 'undefined') {
      setLayouts(layout);
    } else {
      setLayouts({
        ...globalLayouts,
        [getBreakpoint()]: layout
      });
    }
  };
  const onResizeStop = () => {};

  return (
    <Page>
      <Breadcrumb data={[{ title: 'Widgets preview', link: '/widgets-preview' }, { title: 'News' }]} />
      <Container>
        {widgets.length > 0 ? (
          <div className={`dashboard-widgets`} ref={gridLayoutRef}>
            <ResponsiveReactGridLayout
              style={{ margin: 0 }}
              {...gridConfigs}
              layouts={globalLayouts}
              droppingItem={currentDrag}
              onDrop={onDrop}
              onDropDragOver={onDropDragOver}
              onLayoutChange={onLayoutChange}
              onDragStart={onDragStart}
              onDrag={onDrag}
              onDragStop={onDragStop}
              onResize={onResize}
              onResizeStop={onResizeStop}
              onBreakpointChange={onBreakpointChange}
              onWidthChange={onWidthChange}>
              {widgets.map((item, index, items) => {
                return (
                  <div key={item.i}>
                    <GetWidget data={item} />
                  </div>
                );
              })}
            </ResponsiveReactGridLayout>
          </div>
        ) : null}
      </Container>
    </Page>
  );
}
